import jwtDecode from 'jwt-decode';
import { Auth, AuthToken } from '../models/auth';

/** The two facts a caller applying a freshly issued token needs, derived from ONE decode. */
export interface DecodedAuthToken {
  auth: Auth;
  /**
   * True when the token carries the backend's forced-2FA-enrollment claim
   * (TFAConstants.ENROLLMENT_CLAIM, value "true"). Such a token confines the user to the
   * enrollment wizard; the FE uses it to re-establish `enrollmentRequired` on a cookie refresh
   * (the login response signals it out-of-band, but a hard refresh only has the token to go on).
   */
  enrollment: boolean;
}

/**
 * Decode an access token ONCE and derive both the non-secret identity slice and the forced-2FA-
 * enrollment flag. The token-apply / silent-refresh paths need both; deriving them via two separate
 * decodes parsed the same base64url/JSON payload twice on that hot path (the refresh path runs on a
 * timer for the whole session). Throws on an undecodable token; the enrollment flag is absent-safe
 * (a token without the claim is not one).
 */
export function decodeAuthToken(token: string): DecodedAuthToken {
  const decoded = jwtDecode(token) as { [key: string]: any };
  const roles: string[] = (decoded['roles'] || []).map((r: string) => r.toLowerCase().replace('role_', ''));
  return {
    auth: {
      isAuthenticated: true,
      username: decoded['username'] || decoded['email'],
      firstName: decoded['firstName'] || '',
      lastName: decoded['lastName'] || '',
      userId: decoded['userId'],
      roles
    },
    enrollment: String(decoded['enrollment']) === 'true'
  };
}

/** Wrap a raw JWT as the bearer envelope stored in {@link AccessTokenHolder}. */
export function buildAuthToken(rawJwt: string): AuthToken {
  return { type: 'Bearer', value: rawJwt };
}

/**
 * True only when the token can be PROVEN expired: it decodes to a numeric `exp` claim (seconds since
 * epoch) that is in the past, by more than `skewMs`. Used to tell an expired short-lived 2FA challenge
 * token apart from a merely wrong verification code — both surface as a 401, but only the former
 * requires re-authenticating.
 *
 * An ABSENT token returns true (there is nothing usable to submit). A token that is present but can't
 * be decoded, or decodes without a numeric `exp`, returns FALSE: we cannot prove it is expired, so the
 * caller must NOT force a re-authentication. This keeps a single mistyped code on the code step
 * (retryable) instead of bouncing the user all the way back to credentials just because the challenge
 * token happens not to be a JWT / carry an `exp` — a contract we don't control at the HTTP boundary.
 *
 * `skewMs` is an explicit clock-skew grace, deliberately WITHOUT a default: the right bias depends on
 * which verdict is the recoverable one for the caller, so each must state it. The only caller today
 * (LoginComponent.submitCode) passes 0 — there "expired" bounces the user back to credentials while
 * "not expired" strands them retyping a code that can never succeed, so an over-eager expiry verdict is
 * the safe direction. A caller for which a false "expired" is the costly verdict should pass a positive
 * grace instead.
 */
export function isTokenExpired(token: string | undefined | null, skewMs: number): boolean {
  if (!token) {
    return true;
  }
  try {
    const decoded = jwtDecode(token) as { [key: string]: any };
    const exp = decoded['exp'];
    return typeof exp === 'number' && exp * 1000 + skewMs <= Date.now();
  } catch {
    return false;
  }
}
